import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@istari-frontend/ui";
import { ArrowLeft, ArrowRight, Circle, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { ConversationStatus } from "~/ai-chat/model/conversation";
import { cn } from "~/utils/cn";

export interface ChatTab {
  // Stable client-side identity so empty (unsaved) chats persist as distinct tabs.
  key: string;
  title: string;
  status: ConversationStatus | null;
  isActive: boolean;
}

interface ChatTabsProps {
  tabs: ChatTab[];
  onSelect: (key: string) => void;
  onClose: (key: string) => void;
  onNew: () => void;
}

// Status dot on a tab: amber = processing/running now, green = a completed turn,
// red = a terminal error, neutral gray for a fresh/idle chat.
function dotClass(status: ConversationStatus | null): string {
  if (status === "running") return "fill-warning text-warning";
  if (status === "completed") return "fill-success text-success";
  if (status === "error") return "fill-destructive text-destructive";
  return "fill-muted text-muted-foreground";
}

// The open-chats strip: one tab per chat open this session (saved or a fresh unsaved
// one), a status dot, and a `+` to start another. Switching never stops a running
// chat (they stream in parallel), so several tabs can show the amber dot at once.
// When more tabs are open than fit, scroll arrows appear and off-screen tabs collapse
// into a `+N` overflow menu.
export function ChatTabs({ tabs, onSelect, onClose, onNew }: ChatTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set());

  const measure = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    const viewLeft = el.scrollLeft;
    const viewRight = el.scrollLeft + el.clientWidth;
    const hidden = new Set<string>();
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i] as HTMLElement;
      const left = child.offsetLeft;
      const right = left + child.offsetWidth;
      // Off-screen if its box lies (nearly) entirely outside the scroll viewport.
      if (right <= viewLeft + 4 || left >= viewRight - 4) hidden.add(child.dataset.tabKey ?? "");
    }
    setHiddenKeys(hidden);
  };

  useEffect(() => {
    measure();
    const el = scrollRef.current;
    if (el === null) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // Re-measure when the set of tabs changes.
  }, [tabs]);

  const scrollBy = (delta: number) => scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  const hiddenTabs = tabs.filter((tab) => hiddenKeys.has(tab.key));

  return (
    <div className="border-border flex items-center gap-1 border-b px-2 py-2">
      {canScrollLeft ? (
        <button
          type="button"
          onClick={() => scrollBy(-200)}
          aria-label="Scroll tabs left"
          className="text-muted-foreground hover:bg-accent hover:text-foreground shrink-0 rounded-md p-1"
        >
          <ArrowLeft className="size-4" />
        </button>
      ) : null}
      <div ref={scrollRef} onScroll={measure} className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
        {tabs.map((tab) => (
          <div
            key={tab.key}
            data-tab-key={tab.key}
            className={cn(
              "group/tab flex max-w-48 min-w-0 shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
              tab.isActive
                ? "bg-accent text-accent-foreground font-medium"
                : "text-muted-foreground hover:bg-accent/50",
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(tab.key)}
              aria-current={tab.isActive ? "true" : undefined}
              className="flex min-w-0 flex-1 items-center gap-1.5 text-left outline-none"
            >
              <Circle className={cn("size-2.5 shrink-0", dotClass(tab.status))} aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{tab.title}</span>
            </button>
            <button
              type="button"
              onClick={() => onClose(tab.key)}
              aria-label={`Close ${tab.title}`}
              className="hover:text-foreground shrink-0 rounded opacity-0 transition-opacity group-focus-within/tab:opacity-100 group-hover/tab:opacity-100 focus-visible:opacity-100"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
      {canScrollRight ? (
        <button
          type="button"
          onClick={() => scrollBy(200)}
          aria-label="Scroll tabs right"
          className="text-muted-foreground hover:bg-accent hover:text-foreground shrink-0 rounded-md p-1"
        >
          <ArrowRight className="size-4" />
        </button>
      ) : null}
      {hiddenTabs.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`${hiddenTabs.length} more chats`}
              className="bg-accent/50 text-muted-foreground hover:bg-accent hover:text-foreground shrink-0 rounded-md px-2 py-1.5 text-sm"
            >
              +{hiddenTabs.length}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
            {hiddenTabs.map((tab) => (
              <DropdownMenuItem key={tab.key} onSelect={() => onSelect(tab.key)}>
                <Circle className={cn("size-2.5 shrink-0", dotClass(tab.status))} aria-hidden="true" />
                <span className="truncate">{tab.title}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <button
        type="button"
        onClick={onNew}
        aria-label="Start new chat"
        className="text-muted-foreground hover:bg-accent hover:text-foreground shrink-0 rounded-md p-1.5 transition-colors"
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
}
